import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ChevronLeft, Loader2, Lock, Sparkles, Star, ShieldAlert, Lightbulb } from "lucide-react";
import { toast } from "sonner";

const PERF_LABEL = { 1: "Below", 2: "Meets", 3: "Exceeds" };
const POT_LABEL = { 1: "Limited", 2: "Growth", 3: "High" };

// Box label & accent color per cell. perf rows top→bottom = 3,2,1; potential cols L→R = 1,2,3.
const BOX_INFO = {
  "3,1": { label: "Solid Performer", accent: "border-emerald-300 bg-emerald-50/50" },
  "3,2": { label: "High Performer", accent: "border-emerald-300 bg-emerald-50/50" },
  "3,3": { label: "Star", accent: "border-emerald-400 bg-emerald-100/60" },
  "2,1": { label: "Effective", accent: "border-slate-300 bg-slate-50/50" },
  "2,2": { label: "Core Player", accent: "border-slate-300 bg-slate-50/50" },
  "2,3": { label: "High Potential", accent: "border-violet-300 bg-violet-50/50" },
  "1,1": { label: "Risk", accent: "border-rose-300 bg-rose-50/50" },
  "1,2": { label: "Inconsistent", accent: "border-amber-300 bg-amber-50/50" },
  "1,3": { label: "Rough Diamond", accent: "border-amber-300 bg-amber-50/50" },
};

export default function CalibrationSession() {
  const { id } = useParams();
  const [sess, setSess] = useState(null);
  const [acting, setActing] = useState(false);
  const [editing, setEditing] = useState(null); // participant being edited
  const [insights, setInsights] = useState(null);
  const [insightOpen, setInsightOpen] = useState(false);
  const [insightLoading, setInsightLoading] = useState(false);

  const reload = async () => {
    const r = await api.get(`/calibration/sessions/${id}`);
    setSess(r.data);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const committed = sess?.status === "committed";

  const placeOnBox = async (pid, performance, potential) => {
    if (committed) return;
    try {
      await api.patch(`/calibration/participants/${pid}`, {
        nine_box: { performance, potential },
      });
      // optimistic update
      setSess((prev) => ({
        ...prev,
        participants: prev.participants.map((p) => p.id === pid ? { ...p, nine_box: { performance, potential } } : p),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
  };

  const commit = async () => {
    if (!confirm("Commit this session? Ratings will be locked into reviews and performance history.")) return;
    setActing(true);
    try {
      const r = await api.post(`/calibration/sessions/${id}/commit`, { confirm: true });
      toast.success(`Committed · ${r.data.adjusted} adjustments · ${r.data.hipo} HiPo flagged`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally {
      setActing(false);
    }
  };

  const runInsights = async () => {
    setInsightOpen(true);
    setInsightLoading(true);
    try {
      const r = await api.post("/ai/calibration-insights", { session_id: id });
      setInsights(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
      setInsightOpen(false);
    } finally {
      setInsightLoading(false);
    }
  };

  const grouped = useMemo(() => {
    const out = {};
    for (let perf = 1; perf <= 3; perf++) {
      for (let pot = 1; pot <= 3; pot++) {
        out[`${perf},${pot}`] = [];
      }
    }
    (sess?.participants || []).forEach((p) => {
      const key = `${p.nine_box?.performance ?? 2},${p.nine_box?.potential ?? 2}`;
      if (out[key]) out[key].push(p);
    });
    return out;
  }, [sess]);

  const adjustedCount = (sess?.participants || []).filter((p) => p.adjusted).length;
  const hipoCount = (sess?.participants || []).filter((p) => p.is_hipo).length;

  if (!sess) return <div className="text-sm text-slate-500">Loading session…</div>;

  return (
    <div className="max-w-7xl space-y-6" data-testid="calibration-session-page">
      <Link to="/calibration" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
        <ChevronLeft className="w-3 h-3" /> Back to sessions
      </Link>

      <div className="axia-card p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              {committed
                ? <span className="axia-pill bg-emerald-50 text-emerald-700 border-emerald-200 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Committed</span>
                : <span className="axia-pill bg-amber-50 text-amber-700 border-amber-200">In progress</span>}
              <span className="axia-overline">{sess.phase.replace("_", "-")}</span>
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-2">{sess.name}</h1>
            <p className="text-sm text-slate-500 mt-1.5">{sess.cycle_name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={runInsights} data-testid="ai-insights-btn">
              <Sparkles className="w-3.5 h-3.5 mr-2" /> AI Insights
            </Button>
            {!committed && (
              <Button onClick={commit} disabled={acting} data-testid="commit-session-btn">
                {acting && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
                <Lock className="w-3.5 h-3.5 mr-2" /> Commit session
              </Button>
            )}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
          <Stat label="Participants" value={sess.participants?.length || 0} />
          <Stat label="Adjustments" value={adjustedCount} hint={adjustedCount > 0 ? "ratings changed" : "none yet"} />
          <Stat label="HiPo flagged" value={hipoCount} />
        </div>
      </div>

      {/* 9-box grid */}
      <div className="axia-card p-6">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="font-display text-base font-semibold text-slate-900">9-box · Performance × Potential</h2>
          <span className="text-xs text-slate-400">drag chips between boxes to re-place</span>
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-2">
          <div className="flex flex-col-reverse justify-around items-end pr-2 text-[11px] uppercase tracking-wider text-slate-500 py-2">
            <span>Below ↓</span><span>Meets</span><span>Exceeds ↑</span>
          </div>
          <div>
            <div className="grid grid-cols-3 gap-3">
              {[3, 2, 1].map((perf) => (
                <React.Fragment key={perf}>
                  {[1, 2, 3].map((pot) => {
                    const k = `${perf},${pot}`;
                    const info = BOX_INFO[k];
                    const items = grouped[k] || [];
                    return (
                      <div
                        key={k}
                        className={`min-h-[140px] rounded-lg border-2 ${info.accent} p-3 transition`}
                        onDragOver={(e) => { if (!committed) e.preventDefault(); }}
                        onDrop={(e) => {
                          if (committed) return;
                          const pid = e.dataTransfer.getData("text/plain");
                          if (pid) placeOnBox(pid, perf, pot);
                        }}
                        data-testid={`9box-cell-${perf}-${pot}`}
                      >
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{info.label}</div>
                        <div className="text-[10px] text-slate-400 mb-2">{PERF_LABEL[perf]} · {POT_LABEL[pot]}</div>
                        <div className="space-y-1.5">
                          {items.map((p) => (
                            <Chip
                              key={p.id}
                              p={p}
                              committed={committed}
                              onEdit={() => setEditing(p)}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
            <div className="grid grid-cols-3 mt-2 text-[11px] uppercase tracking-wider text-slate-500 text-center">
              <span>← Limited potential</span><span>Growth</span><span>High potential →</span>
            </div>
          </div>
        </div>
      </div>

      {/* Roster table */}
      <div className="axia-card p-6">
        <h2 className="font-display text-base font-semibold text-slate-900 mb-3">Roster</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2">Employee</th>
                <th className="text-left py-2">Department</th>
                <th className="text-left py-2">Manager</th>
                <th className="text-right py-2">Original</th>
                <th className="text-right py-2">Proposed</th>
                <th className="text-center py-2">9-box</th>
                <th className="text-center py-2">HiPo</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(sess.participants || []).map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`roster-row-${p.id}`}>
                  <td className="py-2.5"><div className="font-medium text-slate-900">{p.employee_name}</div><div className="text-xs text-slate-400">{p.designation}</div></td>
                  <td className="py-2.5 text-slate-600">{p.department || "—"}</td>
                  <td className="py-2.5 text-slate-600">{p.manager_name || "—"}</td>
                  <td className="py-2.5 text-right font-mono text-slate-600">{p.original_overall_rating?.toFixed(1) ?? "—"}</td>
                  <td className={`py-2.5 text-right font-mono ${p.adjusted ? "text-amber-700 font-semibold" : "text-slate-600"}`}>
                    {p.proposed_overall_rating?.toFixed(1) ?? "—"}
                  </td>
                  <td className="py-2.5 text-center text-xs text-slate-500">P{p.nine_box?.performance}/Po{p.nine_box?.potential}</td>
                  <td className="py-2.5 text-center">{p.is_hipo && <Star className="w-3.5 h-3.5 text-violet-600 inline" />}</td>
                  <td className="py-2.5 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(p)} disabled={committed} data-testid={`edit-participant-${p.id}`}>Edit</Button>
                  </td>
                </tr>
              ))}
              {(sess.participants || []).length === 0 && (
                <tr><td colSpan={8} className="py-12 text-center text-sm text-slate-500">No participants. Reviews must exist for this cycle/phase first.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && <EditParticipantDialog p={editing} onClose={() => { setEditing(null); reload(); }} />}
      </Dialog>

      {/* AI insights dialog */}
      <Dialog open={insightOpen} onOpenChange={setInsightOpen}>
        <DialogContent className="max-w-2xl" data-testid="insights-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-600" /> Calibration insights</DialogTitle>
            <DialogDescription>Pattern analysis without naming individual employees.</DialogDescription>
          </DialogHeader>
          {insightLoading || !insights ? (
            <div className="py-12 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Analysing…</div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {insights.summary && <p className="text-sm text-slate-700" data-testid="insights-summary">{insights.summary}</p>}
              {(insights.observations || []).length > 0 && (
                <div>
                  <div className="axia-overline">Observations</div>
                  <ul className="mt-1 space-y-1.5">
                    {insights.observations.map((o, i) => (
                      <li key={i} className="text-sm text-slate-700">• {o}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(insights.watchouts || []).length > 0 && (
                <div>
                  <div className="axia-overline text-amber-700">Watchouts</div>
                  <ul className="mt-1 space-y-1.5">
                    {insights.watchouts.map((o, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <ShieldAlert className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" /> {o}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white">
      <div className="axia-overline">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function Chip({ p, committed, onEdit }) {
  const adjusted = p.adjusted;
  const hipo = p.is_hipo;
  return (
    <div
      draggable={!committed}
      onDragStart={(e) => e.dataTransfer.setData("text/plain", p.id)}
      onDoubleClick={onEdit}
      className={`group cursor-${committed ? "default" : "grab"} active:cursor-grabbing px-2 py-1 rounded-md text-xs flex items-center gap-1.5 ${
        adjusted ? "bg-amber-100 text-amber-900 border border-amber-200" : "bg-white text-slate-800 border border-slate-200"
      } hover:border-slate-400 transition`}
      title={`${p.employee_name} · drag to re-place, double-click to edit`}
      data-testid={`chip-${p.id}`}
    >
      {hipo && <Star className="w-3 h-3 text-violet-600 shrink-0" />}
      <span className="truncate">{p.employee_name}</span>
      <span className="font-mono text-[10px] text-slate-500 shrink-0">{p.proposed_overall_rating?.toFixed(1) ?? "—"}</span>
    </div>
  );
}

function EditParticipantDialog({ p, onClose }) {
  const [rating, setRating] = useState(p.proposed_overall_rating ?? p.original_overall_rating ?? 3);
  const [hipo, setHipo] = useState(!!p.is_hipo);
  const [notes, setNotes] = useState(p.notes || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/calibration/participants/${p.id}`, {
        proposed_overall_rating: Number(rating),
        is_hipo: hipo,
        notes: notes || null,
      });
      toast.success("Saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-md" data-testid="edit-participant-dialog">
      <DialogHeader>
        <DialogTitle>{p.employee_name}</DialogTitle>
        <DialogDescription>{p.designation} · {p.department}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="bg-slate-50 rounded-md p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Original</div>
            <div className="font-mono text-base font-semibold text-slate-900 mt-0.5">{p.original_overall_rating?.toFixed(1) ?? "—"}</div>
          </div>
          <div>
            <Label>Proposed rating (1–5)</Label>
            <Input type="number" step="0.5" min="1" max="5" value={rating} onChange={(e) => setRating(e.target.value)} data-testid="proposed-rating-input" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={hipo} onChange={(e) => setHipo(e.target.checked)} className="accent-slate-900" data-testid="hipo-checkbox" />
          <Star className="w-3.5 h-3.5 text-violet-600" /> Flag as HiPo (high potential)
        </label>
        <div>
          <Label>Calibration notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Why was this adjusted?" data-testid="notes-textarea" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="save-participant-btn">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Save
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
